import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Camera
} from 'lucide-react';
import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { DialogDescription } from '@radix-ui/react-dialog';

interface LensComponentsModalProps {
  deviceId: string;
  subDeviceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

// Temporary lens interface until API is fully implemented
interface LensDto {
  id?: string;
  name?: string;
}

export const LensComponentsModal = ({
  deviceId,
  subDeviceId,
  open,
  onOpenChange,
  onClose,
}: LensComponentsModalProps) => {
  // Fetch fresh device data
  const { data: subDevices } = useQuery(FetchSubDevicesQuery({ deviceId }));
  const device = subDevices?.find(d => d.id === subDeviceId);
  const lenses: LensDto[] = (device as any)?.lenses || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-gray-500" />
              Lens Management - {device?.name || 'Unknown Device'}
            </DialogTitle>
            <DialogDescription className="text-gray-400 mt-2">
              Lens management API is currently being implemented. This feature will be available soon.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 px-6">
            <div className="max-w-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lenses.length > 0 ? (
                    lenses.map((lens: LensDto, index) => (
                      <TableRow key={lens.id || `lens-${index}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Camera className="h-4 w-4 text-gray-500" />
                            <span className="font-medium">{lens.name || `Lens ${index + 1}`}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="text-gray-500 text-sm">
                            Coming soon
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8">
                        <div className="text-gray-500">
                          <Camera className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No lenses configured</p>
                          <p className="text-sm mt-1">API implementation in progress</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};